import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  createManualEntry,
  deleteManualEntry,
  listManualEntries,
  updateManualEntry,
  type ManualEntryRequest,
  type ManualFinancialEntry,
} from "../onboarding-api";

export interface UseManualEntriesOptions {
  enabled: boolean;
  // Optional so test renders and unauthenticated previews can pass it through
  // without a fallback closure at the call site (which would churn identity
  // every render). When absent or when `enabled` is false the hook is inert.
  getToken?: () => Promise<string | null>;
  // Caller-owned refresh policy. Fired after each successful create / update
  // / remove. DashboardPage wires `refreshLiveData` here so the aggregate
  // header (sourced from `buildDashboardSnapshot()` which reads the cached
  // `/api/users/me` response) AND the readiness store both re-fetch in one
  // shot — matching `useProfileEdit`'s onSuccess semantics. The hook itself
  // is refresh-agnostic; if absent, the local entry list still updates but
  // no dashboard / readiness refetch fires.
  onMutationSuccess?: () => void | Promise<void>;
  // Monotonic counter the caller bumps to trigger an external reload (e.g.
  // after Plaid Link succeeds and inserts Plaid-sourced contributor rows
  // server-side). Bumping `refreshKey` re-fetches /api/financials/manual-entries
  // and replaces the local list — without this, the list shows last-mount
  // state until the user navigates away and back.
  // DashboardPage passes `dashboardRefreshNonce` so a single bump in
  // `refreshLiveData` refreshes readiness, the /api/users/me bootstrap, and
  // the manual-entries list in lockstep.
  refreshKey?: number;
}

export interface UseManualEntriesResult {
  entries: ManualFinancialEntry[];
  isLoading: boolean;
  error: Error | null;
  create: (req: ManualEntryRequest) => Promise<void>;
  /** Callers that checkpoint a created row can retain its server ID. */
  createWithResult: (req: ManualEntryRequest) => Promise<ManualFinancialEntry>;
  update: (
    id: string,
    req: Omit<ManualEntryRequest, "entry_kind">,
  ) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

// useManualEntries owns the Profile contributor list for a profile. GET returns
// manual, document, and Plaid contributors; mutations still act only on manual
// rows and patch that local list in place.
//
// Why not split into per-kind hooks? The backend returns the union in one
// response, and Profile rows render slices of the same list. Loading once
// is cheaper and avoids three separate flickers.
//
// Stable callback identity matters: `create`/`update`/`remove` are passed
// through multiple component layers, so we wrap them in useCallback over a
// ref-stored `getToken`. Caller-side closure churn on `getToken` (which
// Clerk's useAuth does naturally) no longer cascades into re-renders.
export function useManualEntries({
  enabled,
  getToken,
  onMutationSuccess,
  refreshKey,
}: UseManualEntriesOptions): UseManualEntriesResult {
  const [entries, setEntries] = useState<ManualFinancialEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const hasFetched = useRef(false);

  // Keep the latest getToken in a ref so the load effect and the
  // create/update/remove callbacks read it at call time rather than capture
  // it at definition time. This is the "fire once on enable" pattern.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getTokenRef.current?.();
      if (!token) return;
      const fetched = await listManualEntries(token);
      setEntries(fetched);
      setError(null);
    } catch (e) {
      setError(
        e instanceof Error ? e : new Error("Failed to load manual entries"),
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || hasFetched.current) return;
    hasFetched.current = true;
    void fetchEntries();
  }, [enabled, fetchEntries]);

  // External reload trigger. Skip the very first render because the
  // initial-load effect above already handles it; subsequent bumps from
  // the caller (e.g. after Plaid Link onLinked) re-fetch the union list
  // so newly-created Plaid-sourced rows appear without a hard refresh.
  const previousRefreshKey = useRef<number | undefined>(refreshKey);
  useEffect(() => {
    if (!enabled || refreshKey === undefined) return;
    if (previousRefreshKey.current === refreshKey) return;
    previousRefreshKey.current = refreshKey;
    if (!hasFetched.current) return;
    void fetchEntries();
  }, [enabled, refreshKey, fetchEntries]);

  const tokenOrThrow = useCallback(async () => {
    const token = await getTokenRef.current?.();
    if (!token) throw new Error("Not authenticated");
    return token;
  }, []);

  // Ref-stored callback so identity churn at the caller doesn't recreate
  // create / update / remove on every render. The callback is invoked after
  // local entry state has been patched in (so a caller-driven dashboard
  // refresh races with — but doesn't block — the user-visible list update).
  // Failures are swallowed because the entry mutation itself already
  // succeeded; surfacing a refresh error would mislead the user about save
  // state. ADR-027 sanctions manual-entry as a refresh trigger.
  const onMutationSuccessRef = useRef(onMutationSuccess);
  useEffect(() => {
    onMutationSuccessRef.current = onMutationSuccess;
  }, [onMutationSuccess]);

  const notifyMutationSuccess = useCallback(async () => {
    const cb = onMutationSuccessRef.current;
    if (!cb) return;
    try {
      await cb();
    } catch {
      // Refresh is fire-and-forget for the manual-entry callsite.
    }
  }, []);

  const createWithResult = useCallback(
    async (req: ManualEntryRequest) => {
      const token = await tokenOrThrow();
      const created = await createManualEntry(req, token);
      setEntries((prev) => [...prev, created]);
      await notifyMutationSuccess();
      return created;
    },
    [tokenOrThrow, notifyMutationSuccess],
  );

  const create = useCallback(
    async (req: ManualEntryRequest) => {
      await createWithResult(req);
    },
    [createWithResult],
  );

  const update = useCallback(
    async (id: string, req: Omit<ManualEntryRequest, "entry_kind">) => {
      const token = await tokenOrThrow();
      const updated = await updateManualEntry(id, req, token);
      setEntries((prev) => prev.map((e) => (e.id === id ? updated : e)));
      await notifyMutationSuccess();
    },
    [tokenOrThrow, notifyMutationSuccess],
  );

  const remove = useCallback(
    async (id: string) => {
      const token = await tokenOrThrow();
      await deleteManualEntry(id, token);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      await notifyMutationSuccess();
    },
    [tokenOrThrow, notifyMutationSuccess],
  );

  return useMemo(
    () => ({
      entries,
      isLoading,
      error,
      create,
      createWithResult,
      update,
      remove,
    }),
    [entries, isLoading, error, create, createWithResult, update, remove],
  );
}
